import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';

import { getEmployerDiscountState } from '../../../../../features/benefit/get_employer_discount';
import useNotification from '../../../../../core/hooks/use_notification';
import { getAllPodsState } from '../../../../../features/pod/get_all_pods';
import TextFieldFormik from '../../../../common/text_field';
import { SelectFieldFormik, SelectFieldMultipleFormik, SelectFieldMultipleFormikWithMore } from '../../../../common/select_field';
import { SubmitButton } from '../../../../common/button';
import { deleteEmployerDiscountAction, deleteEmployerDiscountState } from '../../../../../features/benefit/delete_employer_discount';
import { deleteEmployerDiscountNotificationData, updateEmployerDiscountNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { updateEmployerDiscountAction, updateEmployerDiscountState } from '../../../../../features/benefit/update_employer_discount';
import { getAllHubsState } from "../../../../../features/pop/get_all_hubs";
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';

const discountTypeOptions = [
    { value: 'PERCENTAGE', label: 'Procentowa' },
    { value: 'AMOUNT', label: 'Kwotowa' },
];

const limitTypeOptions = [
    { value: 'NO_LIMIT', label: 'Bez limitu' },
    { value: 'DAILY_LIMIT', label: 'Limit dzienny' },
    { value: 'WEEKLY_LIMIT', label: 'Limit tygodniowy' },
];

const DiscountForm = ({ employerId, refreshDiscount }) => {
    const { showNotification } = useNotification();
    const dispatch = useDispatch();

    const getAllPods = useSelector(getAllPodsState);
    const getAllHubs = useSelector(getAllHubsState);
    const getEmployerDiscount = useSelector(getEmployerDiscountState);
    const updateEmployerDiscount = useSelector(updateEmployerDiscountState);
    const deleteEmployerDiscount = useSelector(deleteEmployerDiscountState);

    const initialValues = {
        podIds: getEmployerDiscount?.data?.podIds || [],
        hubIds: getEmployerDiscount?.data?.hubIds || [],
        discountType: getEmployerDiscount?.data?.discountType || '',
        discount: getEmployerDiscount?.data?.discount || '',
        limitType: getEmployerDiscount?.data?.limitType || ''
    };

    const schema = Yup.object().shape({
        podIds: Yup.array(),
        hubIds: Yup.array(),
        discountType: Yup.string().required(),
        discount: Yup.number().required(),
        limitType: Yup.string().required()
    });

    const onLimitTypeChange = (value, formik) => {
        if (value === limitTypeOptions[0].value) {
            formik.setFieldValue('discountType', discountTypeOptions[0].value);
        } else {
            formik.setFieldValue('discountType', discountTypeOptions[1].value);
        }
    };

    const onDeleteEmployerDiscount = () => {
        dispatch(deleteEmployerDiscountAction(employerId))
            .then(response => {
                showNotification(deleteEmployerDiscountNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshDiscount();
            });
    };

    const onSubmit = (values) => {
        let formValues = { ...values };

        if (formValues.discountType === 'AMOUNT') {
            formValues.currency = 'PLN';
        }

        dispatch(updateEmployerDiscountAction({ employerId, form: formValues }))
            .then(response => {
                showNotification((updateEmployerDiscountNotificationData(response)));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshDiscount();
            });
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            { (formik) => (
                <Box component={ Form }
                     sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>

                            <SectionPaper title="Warunki operacyjne i usunięcie obniżki">
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Zarządzanie regułą naliczania rabatu dla podmiotu korporacyjnego:
                                        </Typography>
                                    </Box>
                                    <SubmitButton
                                        type="button"
                                        onClick={ () => onDeleteEmployerDiscount() }
                                        isLoading={ deleteEmployerDiscount?.loading }
                                        sx={ { width: 160 } }
                                        color="error"
                                    >
                                        Usuń obniżkę
                                    </SubmitButton>
                                </Stack>
                            </SectionPaper>

                            <SectionPaper title="Konfiguracja wielkości obniżki i limitów">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <SelectFieldFormik
                                            name="limitType"
                                            label="Typ limitu"
                                            options={ limitTypeOptions }
                                            required
                                            onChange={ (value) => onLimitTypeChange(value, formik) }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik
                                            name="discount"
                                            label="Obniżka"
                                            type="number"
                                            required
                                            inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <SelectFieldFormik
                                            name="discountType"
                                            label="Typ obniżki"
                                            options={ discountTypeOptions }
                                            required
                                            disabled
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Zasięg handlowy reguły (Pody i Huby)">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 }>
                                        <SelectFieldMultipleFormikWithMore
                                            name="podIds"
                                            label="Wybierz pody - wiele"
                                            defaultOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId != null).map(pod => ({
                                                value: pod?.id,
                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + pod?.device?.serviceId
                                            })) }
                                            extraOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId == null).map(pod => ({
                                                value: pod?.id,
                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - brak urządzenia'
                                            })) }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 }>
                                        <SelectFieldMultipleFormik
                                            name="hubIds"
                                            label="Wybierz huby - wiele"
                                            options={ getAllHubs?.data?.map(hub => ({
                                                value: hub?.id,
                                                label: hub?.name
                                            })) }
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                            <SubmitButton isLoading={ updateEmployerDiscount?.loading } sx={ { width: 160 } }>
                                Zapisz zmiany
                            </SubmitButton>
                        </Box>
                    </BottomActionBar>

                </Box>
            ) }
        </Formik>
    );
};

export default DiscountForm;